import { useEffect, useRef, useState } from "react";
import { Html5Qrcode } from "html5-qrcode";

// Safe stop: html5-qrcode throws synchronous errors if not running
const safeStop = (scanner) => {
  try {
    const state = scanner.getState();
    // 2 = SCANNING, 3 = PAUSED
    if (state === 2 || state === 3) {
      return scanner.stop().catch(() => {});
    }
  } catch {
    // getState() itself can throw if scanner is in bad state
    try { return scanner.stop().catch(() => {}); } catch { /* ignore */ }
  }
  return Promise.resolve();
};

export default function QrScanner({ onScan, onError, onClose }) {
  const containerRef = useRef(null);
  const scannerRef = useRef(null);
  const [camError, setCamError] = useState(null);

  useEffect(() => {
    const regionId = "qr-reader-" + Math.random().toString(36).slice(2, 8);
    if (containerRef.current) {
      containerRef.current.id = regionId;
    }

    let cancelled = false;
    const scanner = new Html5Qrcode(regionId);
    scannerRef.current = scanner;

    // Suppress known errors from html5-qrcode (dev overlay only)
    const suppressError = (e) => {
      const msg = e?.message || e?.reason?.message || String(e?.reason || "");
      if (msg.includes("Cannot stop") || msg.includes("fetching process") || msg.includes("aborted")) {
        e.preventDefault?.();
        e.stopPropagation?.();
        return true;
      }
    };
    window.addEventListener("error", suppressError, true);
    window.addEventListener("unhandledrejection", suppressError, true);

    // Force-kill camera tracks directly from video elements
    const killTracks = () => {
      try {
        containerRef.current?.querySelectorAll("video").forEach((v) => {
          if (v.srcObject) {
            v.srcObject.getTracks().forEach((t) => t.stop());
            v.srcObject = null;
          }
        });
      } catch { /* ignore */ }
    };

    scanner
      .start(
        { facingMode: "environment" },
        { fps: 10, qrbox: { width: 220, height: 220 } },
        (text) => {
          if (!cancelled) {
            safeStop(scanner).finally(killTracks);
            onScan(text);
          }
        },
        () => {}
      )
      .then(() => {
        if (cancelled) safeStop(scanner).finally(killTracks);
      })
      .catch((err) => {
        if (!cancelled) {
          setCamError(err?.message || "Kamera konnte nicht gestartet werden");
          if (onError) onError(err);
        }
      });

    return () => {
      cancelled = true;
      safeStop(scanner).finally(() => {
        killTracks();
        try { scanner.clear(); } catch { /* ignore */ }
      });
      setTimeout(() => {
        killTracks();
        window.removeEventListener("error", suppressError, true);
        window.removeEventListener("unhandledrejection", suppressError, true);
      }, 1000);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleClose = () => {
    const scanner = scannerRef.current;
    if (scanner) {
      safeStop(scanner);
    }
    // Force-kill tracks directly
    try {
      containerRef.current?.querySelectorAll("video").forEach((v) => {
        if (v.srcObject) {
          v.srcObject.getTracks().forEach((t) => t.stop());
          v.srcObject = null;
        }
      });
    } catch { /* ignore */ }
    if (onClose) onClose();
  };

  return (
    <div className="relative rounded-lg overflow-hidden bg-black" data-testid="qr-scanner">
      <div ref={containerRef} style={{ width: "100%", minHeight: 260 }} />
      {camError && (
        <div className="absolute inset-0 flex flex-col items-center justify-center bg-gray-900/90 text-white p-4 text-center">
          <p className="text-sm mb-2">Kamera-Fehler</p>
          <p className="text-xs text-gray-300">{camError}</p>
        </div>
      )}
      {onClose && (
        <button
          onClick={handleClose}
          className="absolute top-2 right-2 bg-black/60 text-white rounded-full w-7 h-7 flex items-center justify-center text-sm hover:bg-black/80"
          data-testid="qr-scanner-close"
        >
          ✕
        </button>
      )}
    </div>
  );
}
